import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Ticket, ArrowRight, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import MobileHeader from '../components/MobileHeader';

const JoinEventPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [eventCode, setEventCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleJoin = async () => {
    if (!eventCode || eventCode.trim().length === 0) {
      toast.error('Please enter a valid event code');
      return;
    }

    // Validate format: should be either old formats or new custom prefix format
    const oldSixDigit = /^\d{6}$/.test(eventCode);
    const oldMobileFormat = /^\d{10}_\d{6}$/.test(eventCode);
    const newCustomFormat = /^[a-zA-Z0-9]{6}_\d{6}$/.test(eventCode); // 6 alphanumeric chars + underscore + 6 digits
    
    if (!oldSixDigit && !oldMobileFormat && !newCustomFormat) {
      toast.error('Please enter a valid event code (e.g., ABC123_678901 or THIRUV_123456)');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/events/by-code/${eventCode}`);
      
      if (response.data) {
        toast.success('Event found! Redirecting...');
        navigate(`/event/${response.data.id}`);
      }
    } catch (error) {
      console.error('Error finding event:', error);
      if (error.response?.status === 404) {
        toast.error('Event not found. Please check the code and try again.');
      } else {
        toast.error('Failed to join event. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleJoin();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pb-20">
      <MobileHeader title="Join Event" />
      
      <div className="pt-20 px-4 max-w-2xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-contribo-navy hover:text-contribo-gold transition-colors font-medium"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>

        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full mb-4 shadow-md-3">
            <Ticket className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Join an Event
          </h1>
          <p className="text-gray-600">
            Enter the event code to RSVP and contribute
          </p>
        </div>

        {/* Join Event Card */}
        <Card className="shadow-md-2 border-0">
          <CardHeader className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              Event Code
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Event Code Input */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">
                Enter Event Code
              </label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="e.g., THIRUV_123456 or ABC123_678901"
                  value={eventCode}
                  onChange={(e) => setEventCode(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toUpperCase().slice(0, 13))}
                  onKeyPress={handleKeyPress}
                  className="text-center text-xl tracking-wide font-semibold h-14 text-gray-900 border-2 focus:border-blue-900"
                  maxLength={13}
                  disabled={loading}
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500 text-center">
                Get the event code from your host
              </p>
            </div>

            {/* Join Button */}
            <Button
              onClick={handleJoin}
              disabled={loading || !eventCode}
              className="w-full h-14 bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white text-lg font-semibold shadow-md-2 active:scale-95 transition-transform"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Finding Event...
                </>
              ) : (
                <>
                  Join Event
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="mt-6 bg-amber-50 border-amber-200 shadow-md-1">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">How to Join an Event</h3>
                <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
                  <li>Get the event code from the event host</li>
                  <li>Enter the code in the box above</li>
                  <li>Click "Join Event" to view details and RSVP</li>
                  <li>Make your contribution to the event goals</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Access - Recent Events (if user is logged in) */}
        {user && (
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600 mb-4">or</p>
            <Button
              variant="outline"
              onClick={() => navigate('/my-rsvps')}
              className="text-blue-900 border-blue-900 hover:bg-blue-50"
            >
              View My RSVPs
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinEventPage;
